import { useState, useEffect } from 'react';

type Image = {
  src: string;
  thumb: string;
  alt: string;
  num: number;
};

// Configuration des galeries avec leurs métadonnées
const GALLERY_CONFIG = {
  '2much': {
    title: 'Concert 2Much',
    bgImage: 44,
    altPrefix: '2Much Concert Image',
    folderName: '2Much'
  },
  'eclipse3': {
    title: 'Concert Eclipse 3',
    bgImage: 4,
    altPrefix: 'Eclipse 3 Concert Image',
    folderName: 'Eclipse3'
  },
  'hdp': {
    title: 'Concert Heure de pointe',
    bgImage: 0,
    altPrefix: 'HDP Concert Image',
    folderName: 'HDP'
  },
  'nachave': {
    title: 'Concert Nachave Présente',
    bgImage: 3,
    altPrefix: 'Nachave Concert Image',
    folderName: 'Nachave'
  },
  'nava': {
    title: 'Concert Nava',
    bgImage: 12,
    altPrefix: 'Nava Concert Image',
    folderName: 'Nava'
  },
  'peacock': {
    title: 'Festival Peacock',
    bgImage: 13,
    altPrefix: 'Peacock Festival Image',
    folderName: 'Peacock'
  },
  'we-love-green-2k25': {
    title: 'Festival WE LOVE GREEN 2K25',
    bgImage: 14,
    altPrefix: 'WE LOVE GREEN Festival Image',
    folderName: 'WeLoveGreen2K25'
  }
};

function createImageMap(context: any) {
  const images: Record<string, string> = {};
  
  context.keys().forEach((item: string | number) => {
    try {
      const module = context(item);
      const imageSrc = module.default || module;
      if (imageSrc) {
        images[item] = imageSrc;
      }
    } catch (error) {
      console.error('Error loading:', item, error);
    }
  });
  
  return images;
}

// Fonction pour obtenir les contextes selon le dossier
const getContextsForFolder = (folderName: string) => {
  try {
    switch (folderName) {
      case '2Much':
        return {
          full: require.context('../../img/evenement/2MUCH', false, /\.webp$/),
          thumbs: require.context('../../img/evenement/2MUCH/thumbs', false, /\.webp$/)
        };
      case 'Eclipse3':
        return {
          full: require.context('../../img/evenement/ECLIPSE3', false, /\.webp$/),
          thumbs: require.context('../../img/evenement/ECLIPSE3/thumbs', false, /\.webp$/)
        };
      case 'HDP':
        return {
          full: require.context('../../img/evenement/HDP', false, /\.webp$/),
          thumbs: require.context('../../img/evenement/HDP/thumbs', false, /\.webp$/)
        };
      case 'Nachave':
        return {
          full: require.context('../../img/evenement/NACHAVE', false, /\.webp$/),
          thumbs: require.context('../../img/evenement/NACHAVE/thumbs', false, /\.webp$/)
        };
      case 'Nava':
        return {
          full: require.context('../../img/evenement/NAVA', false, /\.webp$/),
          thumbs: require.context('../../img/evenement/NAVA/thumbs', false, /\.webp$/)
        };
      case 'Peacock':
        return {
          full: require.context('../../img/evenement/PEACOCK', false, /\.webp$/),
          thumbs: require.context('../../img/evenement/PEACOCK/thumbs', false, /\.webp$/)
        };
      case 'WeLoveGreen2K25':
        return {
          full: require.context('../../img/evenement/WE-LOVE-GREEN-2K25', false, /\.webp$/),
          thumbs: require.context('../../img/evenement/WE-LOVE-GREEN-2K25/thumbs', false, /\.webp$/)
        };
      default:
        throw new Error(`Dossier non supporté: ${folderName}`);
    }
  } catch (error) {
    console.error(`Erreur lors du chargement du contexte pour ${folderName}:`, error);
    throw error;
  }
};

export const useGalleryConcertImages = (galleryPath: string) => {
  const [images, setImages] = useState<Image[]>([]);
  const [config, setConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadImages = async () => {
      try {
        setLoading(true);
        setError(null);

        // Récupérer la config de la galerie
        const galleryConfig = GALLERY_CONFIG[galleryPath as keyof typeof GALLERY_CONFIG];
        if (!galleryConfig) {
          throw new Error(`Configuration non trouvée pour: ${galleryPath}`);
        }
        setConfig(galleryConfig);

        // Obtenir les contextes pour ce dossier
        const contexts = getContextsForFolder(galleryConfig.folderName);
        
        // Charger les images principales et thumbnails
        const fullImages = createImageMap(contexts.full);
        const thumbImages = createImageMap(contexts.thumbs);

        // Construction du tableau final
        const processedImages = Object.keys(fullImages)
          .filter(key => key.includes('image'))
          .map(key => {
            const match = key.match(/image(\d+)\.webp$/);
            const num = match ? match[1] : '';
            const thumbKey = `./thumb${num}.webp`;
            
            return {
              src: fullImages[key],
              thumb: thumbImages[thumbKey] || fullImages[key],
              alt: `${galleryConfig.altPrefix} ${num}`,
              num: parseInt(num) || 0
            };
          })
          .filter(image => image.src)
          .sort((a, b) => a.num - b.num);

        if (processedImages.length === 0) {
          throw new Error(`Aucune image trouvée dans le dossier ${galleryConfig.folderName}`);
        }

        setImages(processedImages);
      } catch (err) {
        console.error('Erreur lors du chargement de la galerie:', err);
        setError(err instanceof Error ? err.message : 'Erreur inconnue');
      } finally {
        setLoading(false);
      }
    };

    if (galleryPath) {
      loadImages();
    }
  }, [galleryPath]);

  return { images, config, loading, error };
};